import React, { useState } from 'react';
import * as Routes from '@/modules/routes';
import Icon from '@/components/ui/Icon';
import SPAButton from '../../shared/SPAButton';
import SPADialog from '../../shared/SPADialog';
import SPADropdown from '../../shared/SPADropdown';
import { useProgramSummary } from '../../shared/ProgramTree/usePrograms';
import { ProgramLesson } from './types';
import { useRemoveLesson } from './useProgramLessons';

interface LessonActionsMenuProps {
  lesson: ProgramLesson;
  programId: number;
}

// Mirrors the legacy lesson row's Edit link and RemoveLessonModal, folded into one Actions menu
// like the unit header's. Edit still opens the legacy lesson form.
const LessonActionsMenu = ({ lesson, programId }: LessonActionsMenuProps) => {
  const [removeOpen, setRemoveOpen] = useState(false);
  const { title: programTitle } = useProgramSummary(programId);
  const remove = useRemoveLesson(programId);
  const otherProgramCount = Math.max(lesson.program_ids.length - 1, 0);

  const handleRemoveOpenChange = (open: boolean) => {
    if (!open) remove.reset();
    setRemoveOpen(open);
  };

  // The row unmounts with the cache patch, taking the dialog with it — no close needed on success.
  const confirmRemove = () => remove.mutate(lesson);

  return (
    <>
      <SPADropdown>
        <SPADropdown.Trigger asChild>
          <SPAButton aria-label={`Actions for ${lesson.title}`} size="xs" variant="outline">
            Actions
            <Icon icon="ChevronDown" size={12} />
          </SPAButton>
        </SPADropdown.Trigger>
        <SPADropdown.Content align="end" className="tw-w-40">
          <SPADropdown.Item asChild>
            <a href={Routes.edit_admin_lesson_path(lesson.id, { program_id: programId })}>Edit</a>
          </SPADropdown.Item>
          <SPADropdown.Separator />
          <SPADropdown.Item className="tw-text-spa-error" onSelect={() => setRemoveOpen(true)}>
            Remove
          </SPADropdown.Item>
        </SPADropdown.Content>
      </SPADropdown>

      <SPADialog open={removeOpen} onOpenChange={handleRemoveOpenChange}>
        <SPADialog.Content size="sm">
          <SPADialog.Header>
            <SPADialog.Title>Remove Lesson</SPADialog.Title>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3 tw-text-sm tw-text-spa-primary">
            <p className="tw-m-0">
              This action will not delete the lesson completely. If you need to recover it, log a request
              with the software team.
            </p>
            <p className="tw-m-0">
              This lesson is used in {otherProgramCount} other {otherProgramCount === 1 ? 'program' : 'programs'}.
            </p>
            <SPADialog.Description>
              Are you sure you want to remove <strong>{lesson.title}</strong> from <strong>{programTitle}</strong>?
            </SPADialog.Description>
            {remove.isError && (
              <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-spa-error" role="alert">
                The lesson could not be removed. Try again.
              </p>
            )}
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPAButton>Cancel</SPAButton>
            </SPADialog.Close>
            <SPAButton disabled={remove.isLoading} variant="danger" onClick={confirmRemove}>
              Yes, remove lesson
            </SPAButton>
          </SPADialog.Footer>
        </SPADialog.Content>
      </SPADialog>
    </>
  );
};

export default LessonActionsMenu;
